import {MDIcon} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {Form} from '@mintdata-wsp/wsp/ui/components/forms/data/Form';
import {DropdownItem} from '@mintdata-wsp/wsp/ui/components/forms/Dropdown';
import {FieldValidationResult} from '@mintdata-wsp/wsp/ui/components/forms/validators/OldValidators';
import {ButtonStyle, WSPButton} from '@mintdata-wsp/wsp/ui/components/general/WSPButton';
import {BaseComponentWithContext} from '@mintdata/mint-kit/api/components/BaseComponentWithContext';
import {Popover} from '@mintdata/mint-kit/impl/components/popover/Popover';
import {PopupAnchor, PopupSize, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import * as React from 'react';

const styles = require('./Input.less');
const cx = classnames.bind(styles);

export enum ErrorPopupSide
{
    Right = 1,
    Bottom = 2,
}

export enum ErrorStyle
{
    BdpForms = 1,
    PropPanel = 2,
}

export enum InputStyle
{
    Box = 1,
    Simple = 2,
    NoBorder = 3,
    Dialog = 4,
}

export enum InputSize
{
    Small = 1, // 24
    Medium = 2, // 32
    Default = 3, // 36
    Large = 4, // 40
    ExtraLarge = 5, // 48
}

const styleSuffixes =
    {
        [InputStyle.Simple]: 'simpleStyle',
        [InputStyle.Box]: 'boxStyle',
        [InputStyle.NoBorder]: 'noBorderStyle',
        [InputStyle.Dialog]: 'dialogStyle',
    };

const sizeSuffixes =
    {
        [InputSize.Small]: 'smallSize',
        [InputSize.Medium]: 'mediumSize',
        [InputSize.Default]: 'defaultSize',
        [InputSize.Large]: 'largeSize',
        [InputSize.ExtraLarge]: 'extraLargeSize',
    };

const errorSuffixes = {
    [ErrorStyle.BdpForms]: 'errorBdpForms',
    [ErrorStyle.PropPanel]: 'errorPropPanel',
};

export class InputState
{
    isValid = true;
    isFocused:boolean = false;
    value:string = null;
    errorMessage:string = null;
    errorPopupId:string = `errorPopup-${IdUtil.newComponentId()}`;
    errorPopupSide:ErrorPopupSide = ErrorPopupSide.Right;
    errorStyle?:ErrorStyle = ErrorStyle.BdpForms;
    style?:InputStyle = InputStyle.Box;
    size?:InputSize = InputSize.Default;
    isEditMode:boolean = true;
    isDirty:boolean = false;
}

export interface InputProps
{
    icon?:string;
    initialValue?:string;
    isInitiallyFocused?:boolean;
    isInitiallySelected?:boolean;
    placeholder?:string;
    isValidFn?:(value:string) => boolean | FieldValidationResult;
    getErrorMessageFn?:(value:string) => string;
    isPassword?:boolean;
    isReadOnly?:boolean;
    isClickForEdit?:boolean;
    className?:string;
    inputClassName?:string;
    iconClassName?:string;
    errorPopupSide?:ErrorPopupSide;
    errorStyle?:ErrorStyle;
    style?:InputStyle;
    size?:InputSize;
    onChange?:(newValue:string | DropdownItem) => (void);
    onEnterKey?:(e:React.KeyboardEvent<any>) => (void);
    onSetEditMode?:() => (void);
    autoComplete?:string;
    formKey?:string;
    form?:Form;
    multiline?:boolean;
    onRef?:(inputElement:HTMLElement) => void;
    width?:number;
    height?:number;
    showValidationMessage?:boolean;
}

export class Input extends BaseComponentWithContext<InputProps, InputState>
{
    private readonly _state:InputState = new InputState();
    private _inputRef:HTMLInputElement | HTMLTextAreaElement;

    // to prevent cursor jumping after re-render: https://github.com/facebook/react/issues/955#issuecomment-435051198
    private selection = {
        start: -1,
        end: -1
    };

    constructor(props:InputProps)
    {
        super(props);

        let state = new InputState();

        if( props.errorPopupSide )
            state.errorPopupSide = props.errorPopupSide;

        if( props.errorStyle )
            state.errorStyle = props.errorStyle;

        if( props.style )
            state.style = props.style;

        state.size = props.size ? props.size : InputSize.Default;

        if( props.isClickForEdit )
            state.isEditMode = false;

        if( typeof props.initialValue === 'number' || props.initialValue )
            state.value = props.initialValue;

        state.isValid = true;
        state.isDirty = false;

        if( props.isValidFn && typeof props.isValidFn === 'function' )
        {
            let isValid = props.isValidFn(state.value);

            if( isValid === null )
            {
                state.isValid = true;
            }
            else
            {
                if( isValid === true || isValid === false )
                    state.isValid = isValid;
                else
                    state.isValid = isValid.isValid;
            }
        }

        this.state = state;
        this._setFormValue(state.value, state.isValid);

        let {form, formKey} = this.props;
        if( form && formKey )
        {
            form.setFieldKey(formKey, 'elementRef', this);
            form.setFieldKey(formKey, 'initialValue', state.value);
        }
    }

    @bind
    _onInputRefReady(ref:HTMLInputElement | HTMLTextAreaElement)
    {
        const {onRef} = this.props;

        this._inputRef = ref;

        if( onRef )
            onRef(ref);
    }

    componentDidMount()
    {
        this._focusAndOrSelect();
    }

    componentDidUpdate()
    {
        const {selectionStart, selectionEnd} = this._inputRef;
        
        if( this.selection.start === -1 || this.selection.end === -1 )
            return;

        const shouldUpdateSelection = this.selection.start !== selectionStart || this.selection.end !== selectionEnd;
    
        if( shouldUpdateSelection )
        {
            this._inputRef.selectionStart = this.selection.start;
            this._inputRef.selectionEnd = this.selection.end;
        }
    }

    @bind
    private _focusAndOrSelect()
    {
        setTimeout(() =>
        {
            if( this._inputRef )
            {
                const {isInitiallyFocused, isInitiallySelected} = this.props;

                if( isInitiallyFocused )
                    this._inputRef.focus();

                if( isInitiallySelected )
                    this._inputRef.select();
            }
        });
    }

    componentWillReceiveProps(nextProps:InputProps)
    {
        if( nextProps.errorPopupSide )
            this.setState({errorPopupSide: nextProps.errorPopupSide});

        if( nextProps.style )
            this.setState({style: nextProps.style});

        if( this.props.showValidationMessage !== nextProps.showValidationMessage )
            this.checkIsValid();
    }

    @bind
    private _onChange(e:React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>)
    {
        const {isDirty} = this.state;

        let newValue = e.target.value;

        this.selection = {
            start: this._inputRef.selectionStart,
            end: this._inputRef.selectionEnd
        };

        if( !isDirty )
            this.setState({isDirty: true});

        this._fireOnChange(newValue);

        setTimeout(() => this.checkIsValid(newValue));
    }

    @bind
    public setValue(newValue:string):boolean
    {
        return this.checkIsValid(newValue);
    }

    @bind
    public checkIsValid(newValue?):boolean
    {
        if( newValue === undefined )
            newValue = this.state.value;

        let {isValidFn, getErrorMessageFn, form, formKey} = this.props;

        let result:boolean | FieldValidationResult = true;
        let isValid:boolean = true;

        if( isValidFn && typeof isValidFn === 'function' )
        {
            result = isValidFn(newValue);

            if( result === null )
                return;

            if( result === true || result === false )
                isValid = result;
            else
            {
                isValid = result.isValid;
            }

            if( isValid === true )
            {
                this.setState({value: newValue, isValid: true});
            }
            else
            {
                let message = null;
                if( getErrorMessageFn && typeof getErrorMessageFn === 'function' )
                    message = getErrorMessageFn(newValue);
                else if( (result as FieldValidationResult).message )
                    message = (result as FieldValidationResult).message;

                this.setState({value: newValue, isValid: false, errorMessage: message});
            }
        }
        else
        {
            this.setState({value: newValue});
        }

        this._setFormValue(newValue, isValid);
        return isValid;
    }

    private _setFormValue(newValue:any, isValid:boolean)
    {
        let {form, formKey} = this.props;

        if( form && formKey )
            form.onFieldChanged(formKey, newValue, isValid);
    }

    @bind
    private _fireOnChange(newValue:string)
    {
        let {onChange} = this.props;

        if( onChange && typeof onChange === 'function' )
            onChange(newValue);
    }

    private _renderPopupArrow()
    {
        let {errorPopupSide} = this.state;

        if( errorPopupSide === ErrorPopupSide.Right )
            return <div className={cx('arrowLeft', 'arrowOnRight')}/>;
        else if( errorPopupSide === ErrorPopupSide.Bottom )
            return <div className={cx('arrowUp', 'arrowOnBottom')}/>;
    }

    private _renderErrorMessage()
    {
        let {isValid, errorMessage, errorPopupId, errorStyle} = this.state;

        if( isValid || !errorMessage || this.props.showValidationMessage === false )
            return null;

        let {errorPopupSide} = this.state;
        let lines = errorMessage.split('\|');

        return <Popover id={errorPopupId} isModal={false}>
            <div className={cx('popupContainer', errorSuffixes[errorStyle],
                {showOnRight: errorPopupSide === ErrorPopupSide.Right},
                {showOnBottom: errorPopupSide === ErrorPopupSide.Bottom}
            )}>

                {this._renderPopupArrow()}
                <div className={cx('errorMessage')}>
                    {
                        lines.map((line:string, idx:number) =>
                        {
                            let text:string = line ? line.trim() : '';
                            return <div key={idx} className={cx('errorLine')}>{text}</div>;
                        })
                    }
                </div>
            </div>
        </Popover>;
    }

    @bind
    _onFocus()
    {
        this.setState({isFocused: true});
        this.checkIsValid();
    }

    @bind
    _onBlur()
    {
        this.setState({isFocused: false});
        this.checkIsValid();
    }

    @bind
    _computePopupPositioner()
    {
        return (ref:HTMLElement):void =>
        {
            if( !ref )
                return;

            const {popupManager} = this.context;

            let {errorPopupSide, errorPopupId} = this.state;
            let popupFn:any;

            if( errorPopupSide === ErrorPopupSide.Right )
                popupFn = popupManager.position(errorPopupId, SourceAnchor.RightMiddle, PopupAnchor.LeftMiddle);
            else if( errorPopupSide === ErrorPopupSide.Bottom )
                popupFn = popupManager.position(errorPopupId, SourceAnchor.LeftBottom, PopupAnchor.LeftTop, PopupSize.WidthOfSource);
            else
                throw new Error('Unknown errorPopupSide: ' + errorPopupSide);

            popupFn(ref);
        };
    }

    private static _computeClassNames(style:InputStyle, size:InputSize, isValid:boolean, isFocused:boolean,
                                      className:string, isReadOnly:boolean, dirty:boolean, errorStyle:ErrorStyle):string
    {
        const styleSuffix = styleSuffixes[style];

        const sizeSuffix = sizeSuffixes[size];

        const errorStyleSuffix = errorSuffixes[errorStyle];

        const isValidObj:any = {};
        isValidObj[`isNotValid-${styleSuffix}`] = dirty && !isValid;

        const isFocusedObj:any = {};
        isFocusedObj[`isFocused-${styleSuffix}`] = isFocused;

        return cx('inputContainer',
            styleSuffix,
            sizeSuffix,
            isValidObj,
            isFocusedObj,
            errorStyleSuffix,
            className,
            {isReadOnly},
        );
    }

    @bind
    private _onKeyPress(e:React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>)
    {
        let {onEnterKey} = this.props;

        if( onEnterKey && typeof onEnterKey === 'function' )
        {
            if( e.key.toLowerCase() === 'enter' )
                onEnterKey(e);
        }
    }

    @bind
    private _onClickWrapper()
    {
        let {isReadOnly} = this.props;

        if( !isReadOnly )
            this._inputRef.focus();
    }

    @bind
    private _onSwitchToEditMode()
    {
        this.setState({isEditMode: true}, () => this._inputRef.focus());

        const {onSetEditMode} = this.props;

        if( onSetEditMode && typeof onSetEditMode === 'function' )
            onSetEditMode();
    }

    @bind
    private _onMouseUpTextarea()
    {
        if( this._inputRef )
            this._inputRef.focus();
    }

    render()
    {
        let {value, isValid, isFocused, style, size, isEditMode, isDirty, errorStyle} = this.state;

        let {
            placeholder, isPassword, isReadOnly, icon,
            className, inputClassName, iconClassName,
            autoComplete, isClickForEdit, multiline, width, height,
            showValidationMessage
        } = this.props;

        const shouldShowValidationMessage = typeof(showValidationMessage) !== 'undefined' ? showValidationMessage : this.state.isDirty;

        let valueStr = (value === null) ? '' : value;
        let classNames = Input._computeClassNames(style, size, isValid, isFocused,
            className, isReadOnly || !isEditMode, isDirty, errorStyle);
        let hasIcon = icon ? true : false;

        let styleSuffix = styleSuffixes[style];

        let inputStyleObj:any = {};
        inputStyleObj[`input-${styleSuffix}`] = true;

        let noIconStyleObj:any = {};
        noIconStyleObj[`hasNoIcon-${styleSuffix}`] = !hasIcon;

        let multilineStyle:React.CSSProperties = {};

        if( multiline )
        {
            multilineStyle = {
                width,
                height,
            };
        }

        return (
            <div
                className={classNames}
                ref={this._computePopupPositioner()}
                onClick={this._onClickWrapper}
            >
                {icon && <MDIcon className={cx('icon', iconClassName)} name={icon}/>}
                {
                    !multiline &&
                    <input
                        className={cx('input',
                            {hasIcon},
                            noIconStyleObj,
                            inputStyleObj,
                            inputClassName
                        )}
                        ref={this._onInputRefReady}
                        type={isPassword ? 'password' : 'text'}
                        placeholder={placeholder}
                        onFocus={this._onFocus}
                        onBlur={this._onBlur}
                        onChange={this._onChange}
                        onKeyPress={this._onKeyPress}
                        defaultValue={valueStr}
                        autoComplete={autoComplete}
                        readOnly={isReadOnly || !isEditMode}
                        disabled={isReadOnly || !isEditMode}
                    />
                }
                {
                    multiline &&
                    <textarea
                        className={cx('input',
                            {hasIcon},
                            noIconStyleObj,
                            inputStyleObj,
                            inputClassName
                        )}
                        ref={this._onInputRefReady}
                        style={multilineStyle}
                        placeholder={placeholder}
                        onFocus={this._onFocus}
                        onBlur={this._onBlur}
                        onChange={this._onChange}
                        onKeyPress={this._onKeyPress}
                        defaultValue={valueStr}
                        autoComplete={autoComplete}
                        readOnly={isReadOnly || !isEditMode}
                        disabled={isReadOnly || !isEditMode}
                        onMouseUp={this._onMouseUpTextarea}
                    />
                }
                {isClickForEdit && !isEditMode && !isReadOnly && (
                    <WSPButton caption={null}
                               className={cx('editMode')}
                               icon={MDIcons.common.Edit}
                               style={ButtonStyle.SimpleSecondary}
                               onClick={this._onSwitchToEditMode}
                    />

                )}

                {shouldShowValidationMessage && this._renderErrorMessage()}
            </div>
        );
    }
}
